import axios, { type AxiosError, type AxiosResponse, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

import { useAuthStore } from '@/auth/useAuthStore';
import { AUTH_ENDPOINTS } from '@/api/endpoints';
import type { ApiError, RefreshTokenResponse } from '@/types';

// Resolve base URL: in production use current host with fixed :3000, otherwise env or localhost
const resolvedBaseURL = import.meta.env.PROD
  ? `${window.location.protocol}//${window.location.hostname}:3001`
  : (import.meta.env.VITE_API_URL || 'https://lotes.everywhere.software:3001');

// Create axios instance
const apiClient = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 10000,
  withCredentials: true, // Important for http-only cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();

    if (accessToken) {
      // Axios v1 uses AxiosHeaders with a .set API
      // Fall back to direct assignment if not available
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const headers: any = config.headers as any;
      if (typeof headers.set === 'function') {
        headers.set('Authorization', `Bearer ${accessToken}`);
      } else {
        headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    console.log('🔍 Axios Interceptor - Error caught:', {
      status: error.response?.status,
      url: originalRequest.url,
      method: originalRequest.method,
      message: error.response?.data?.message,
    });

    // Identify auth-related endpoints to avoid accidental retry/refresh loops
    const requestUrl = originalRequest.url || '';
    const isPasswordChangeRequest = requestUrl.includes(AUTH_ENDPOINTS.CHANGE_PASSWORD);
    const isLoginRequest = requestUrl.includes(AUTH_ENDPOINTS.LOGIN);
    const isRefreshRequest = requestUrl.includes(AUTH_ENDPOINTS.REFRESH);

    // Never attempt refresh/auto-retry for these endpoints
    if (error.response?.status === 401 && (isPasswordChangeRequest || isLoginRequest || isRefreshRequest)) {
      if (isPasswordChangeRequest) {
        console.log('🔑 Error 401 en cambio de contraseña - omitiendo flujo de cierre de sesión en el interceptor');
        // Devolvemos el error original (AxiosError) porque el flujo de cambio de contraseña
        // espera leer error.response.status en su propio manejo
        return Promise.reject(error);
      }

      if (isLoginRequest) {
        console.log('🔐 Error 401 en login - NO se intenta refresh ni reintento del login');
        // Estandarizamos como ApiError para que la UI muestre el mensaje del backend
        const apiError: ApiError = {
          statusCode: error.response?.status || 401,
          message: (error.response?.data as any)?.message || 'Credenciales inválidas',
          error: (error.response?.data as any)?.error || 'Unauthorized',
          details: (error.response?.data as any)?.details,
        };
        return Promise.reject(apiError);
      }

      // Para /auth/refresh simplemente devolvemos el error original
      return Promise.reject(error);
    }

    // Handle 401 errors (unauthorized) for other endpoints
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔒 Error de autenticación - intentando refrescar token');
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Get refresh token from localStorage
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          throw new Error('No hay token de refresco disponible');
        }

        // Attempt to refresh the token
        const response = await axios.post<RefreshTokenResponse>(
          `${apiClient.defaults.baseURL}${AUTH_ENDPOINTS.REFRESH}`,
          { refreshToken },
          {
            withCredentials: true,
          }
        );

        const { accessToken } = response.data;

        // Update the store with new token
        useAuthStore.getState().setAccessToken(accessToken);

        // Update the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        processQueue(null, accessToken);

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null);
        useAuthStore.getState().clearAuth();

        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Return a standardized error format
    const apiError: ApiError = {
      statusCode: error.response?.status || 500,
      message: error.response?.data?.message || error.message || 'Ha ocurrido un error inesperado',
      error: error.response?.data?.error || 'Error Interno del Servidor',
      details: error.response?.data?.details,
    };

    return Promise.reject(apiError);
  }
);

export default apiClient;
